import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericSettingsPane, GenericSettingsPaneHandle } from '../GenericSettingsPane';
import { PaneProps, PaneHandle } from '../../accountParameter.types';
import { useStoreState } from '../../../../../models/hooks';
import { unflattenTodos } from './unflattenTodos';
import { buildCategoryItems } from './buildTodoListCategoryItems';
import { HIRE_FORM_ROWS, OFFER_STAGE_ROWS } from './todoListConfig';

export const TodoListPane = React.memo(
  forwardRef<PaneHandle, PaneProps>(({ onSave, setDirty, showActions }, ref) => {
    const genericPaneRef = useRef<GenericSettingsPaneHandle>(null);

    const todosConfig = useStoreState((state) => state.app.accounts?.configuration?.todos);

    const { t } = useTranslation('accountParameter');

    useImperativeHandle(
      ref,
      () => ({
        save: () => genericPaneRef.current?.save(),
        reset: () => genericPaneRef.current?.reset(),
      }),
      [],
    );

    const memoizedItems = useMemo(
      () => [
        ...buildCategoryItems('hireForm', 'hireForm', HIRE_FORM_ROWS, todosConfig?.hireForm, t),
        ...buildCategoryItems('offerMade', 'offerMade', OFFER_STAGE_ROWS, todosConfig?.offerMade, t),
        ...buildCategoryItems('offerRejected', 'offerRejected', OFFER_STAGE_ROWS, todosConfig?.offerRejected, t),
        ...buildCategoryItems('offerExpired', 'offerExpired', OFFER_STAGE_ROWS, todosConfig?.offerExpired, t),
      ],
      [todosConfig, t],
    );

    const handleOnSave = useCallback((updates: Record<string, any>) => onSave(unflattenTodos(updates)), [onSave]);

    return (
      <GenericSettingsPane
        ref={genericPaneRef}
        onSave={handleOnSave}
        setDirty={setDirty}
        showInlineActions={showActions}
        items={memoizedItems}
        returnAllRowsValues
      />
    );
  }),
);

TodoListPane.displayName = 'TodoListPane';
